import React, { useEffect, useState } from 'react';
import { Button, IconButton, Tooltip, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { addNewMessage, deleteMessage, getAllMessages, updateMessage } from '../../../../helpers/api/api';
import { formatDateString } from '../../../../helpers/dataBuilders';
import MessageDialog from './messageDialog';

// Traffic-light-style severity styling built entirely from the approved
// brand palette: green for Low, Brass (a "tiny accents" color per the
// brand guide) for Medium, and the brand's one warning/alert tone for High.
const severityStyle = (theme, severity) => {
    if (severity === 'High') {
        return { background: theme.palette.warning.main, color: theme.palette.background.paper };
    }
    if (severity === 'Medium') {
        return { background: theme.palette.brand.brass, color: theme.palette.background.paper };
    }
    return { background: 'transparent', color: theme.palette.success.main, border: `1px solid ${theme.palette.success.main}` };
};

const MessagesTable = () => {
    const theme = useTheme();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingMessage, setEditingMessage] = useState(null);
    const [loadingSave, setLoadingSave] = useState(false);
    const [data, setData] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        async function getAllUrgentMessages() {
            const allMessages = await getAllMessages();
            setData(Array.isArray(allMessages) ? allMessages : []);
            setLoaded(true);
        }

        getAllUrgentMessages();
    }, []);

    const openAddDialog = () => {
        setEditingMessage(null);
        setDialogOpen(true);
    };

    const openEditDialog = (row) => {
        setEditingMessage(row);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingMessage(null);
    };

    const handleSubmit = async ({ messageEn, messageRo, expirationDate, severity, pages }) => {
        setLoadingSave(true);

        if (editingMessage) {
            const formattedExpiration = formatDateString(expirationDate);
            await updateMessage(editingMessage.id, messageEn, messageRo, severity, formattedExpiration, pages);
            setData((prev) =>
                prev.map((row) =>
                    row.id === editingMessage.id
                        ? { ...row, messageEn, messageRo, severity, expirationDate: formattedExpiration, pages }
                        : row
                )
            );
        } else {
            const newMessageId = await addNewMessage(messageEn, messageRo, severity, expirationDate, pages);
            setData((prev) => [
                ...prev,
                {
                    id: newMessageId,
                    datePosted: formatDateString(),
                    messageEn,
                    messageRo,
                    expirationDate: formatDateString(expirationDate),
                    severity,
                    pages,
                },
            ]);
        }

        setLoadingSave(false);
        closeDialog();
    };

    const handleDelete = (id) => {
        if (!window.confirm('Delete this urgent message? This cannot be undone.')) {
            return;
        }
        deleteMessage(id);
        setData((prev) => prev.filter((row) => row.id !== id));
    };

    const cardStyle = {
        border: `1px solid ${theme.palette.brand.border}`,
        borderRadius: '10px',
        backgroundColor: theme.palette.background.paper,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    };

    const chipBaseStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '3px 10px',
        borderRadius: '999px',
        whiteSpace: 'nowrap',
    };

    const pageChipStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: '11px',
        fontWeight: 600,
        color: theme.palette.primary.dark,
        backgroundColor: theme.palette.brand.mist,
        padding: '3px 9px',
        borderRadius: '999px',
        whiteSpace: 'nowrap',
    };

    return (
        <div style={{ maxWidth: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '14px' }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
                    Add Message
                </Button>
            </div>

            {loaded && data.length === 0 && (
                <div
                    style={{
                        fontFamily: theme.typography.paragraphFontFamily,
                        fontSize: '14px',
                        color: theme.palette.text.secondary,
                        padding: '24px',
                        backgroundColor: theme.palette.brand.linen,
                        borderRadius: '10px',
                        textAlign: 'center',
                    }}
                >
                    No urgent messages right now.
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {data.map((row) => (
                    <div key={row.id} style={cardStyle}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                            <span style={{ ...chipBaseStyle, ...severityStyle(theme, row.severity) }}>{row.severity}</span>
                            {row.pages &&
                                row.pages.split(', ').filter(Boolean).map((page) => (
                                    <span key={page} style={pageChipStyle}>
                                        {page}
                                    </span>
                                ))}
                            <span
                                style={{
                                    marginLeft: 'auto',
                                    fontFamily: theme.typography.paragraphFontFamily,
                                    fontSize: '12px',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Expires {row.expirationDate}
                            </span>
                        </div>

                        <div
                            style={{
                                fontFamily: theme.typography.fontFamily,
                                fontSize: '15px',
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {row.messageEn}
                        </div>
                        {row.messageRo && (
                            <div
                                style={{
                                    fontFamily: theme.typography.paragraphFontFamily,
                                    fontSize: '13px',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                {row.messageRo}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px', marginTop: '2px' }}>
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEditDialog(row)}>
                                    <EditOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDelete(row.id)}>
                                    <DeleteOutlineIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>
                ))}
            </div>

            <MessageDialog
                isOpen={dialogOpen}
                handleClose={closeDialog}
                onSubmit={handleSubmit}
                loadingSave={loadingSave}
                editingMessage={editingMessage}
            />
        </div>
    );
};

export default MessagesTable;
